import { z } from 'zod';

/**
 * Garment types seeded into every new shop. A shop may add its own types on top of these
 * (task 8), so this list is a starting point, not a closed universe — but these keys are
 * stable and the seeded measurement templates are keyed off them.
 */
export const SEEDED_GARMENT_TYPE_KEYS = [
  'blouse',
  'saree_fall_pico',
  'kurta',
  'salwar_kameez',
  'churidar',
  'lehenga',
  'gown',
  'shirt',
  'pant',
] as const;

export type SeededGarmentTypeKey = (typeof SEEDED_GARMENT_TYPE_KEYS)[number];

export const seededGarmentTypeKeySchema = z.enum(SEEDED_GARMENT_TYPE_KEYS);

/** A shop-defined garment type key: lowercase snake_case, stable once created. */
export const garmentTypeKeySchema = z
  .string()
  .min(2)
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/, 'Garment type keys are lowercase snake_case');

export const GARMENT_CATEGORIES = ['womens', 'mens', 'kids', 'unisex'] as const;
export type GarmentCategory = (typeof GARMENT_CATEGORIES)[number];
export const garmentCategorySchema = z.enum(GARMENT_CATEGORIES);

export interface SeededGarmentType {
  readonly key: SeededGarmentTypeKey;
  /** Default English label; the UI resolves `garmentType.<key>` through i18next. */
  readonly defaultLabel: string;
  readonly category: GarmentCategory;
  /** Display order in pickers — the common bookings first. */
  readonly sortOrder: number;
  /** Whether this garment normally needs a measurement profile taken. */
  readonly requiresMeasurements: boolean;
}

export const SEEDED_GARMENT_TYPES: readonly SeededGarmentType[] = Object.freeze([
  {
    key: 'blouse',
    defaultLabel: 'Blouse',
    category: 'womens',
    sortOrder: 10,
    requiresMeasurements: true,
  },
  {
    key: 'saree_fall_pico',
    defaultLabel: 'Saree fall & pico',
    category: 'womens',
    sortOrder: 20,
    requiresMeasurements: false,
  },
  {
    key: 'kurta',
    defaultLabel: 'Kurta',
    category: 'unisex',
    sortOrder: 30,
    requiresMeasurements: true,
  },
  {
    key: 'salwar_kameez',
    defaultLabel: 'Salwar kameez',
    category: 'womens',
    sortOrder: 40,
    requiresMeasurements: true,
  },
  {
    key: 'churidar',
    defaultLabel: 'Churidar',
    category: 'womens',
    sortOrder: 50,
    requiresMeasurements: true,
  },
  {
    key: 'lehenga',
    defaultLabel: 'Lehenga',
    category: 'womens',
    sortOrder: 60,
    requiresMeasurements: true,
  },
  {
    key: 'gown',
    defaultLabel: 'Gown',
    category: 'womens',
    sortOrder: 70,
    requiresMeasurements: true,
  },
  {
    key: 'shirt',
    defaultLabel: 'Shirt',
    category: 'mens',
    sortOrder: 80,
    requiresMeasurements: true,
  },
  {
    key: 'pant',
    defaultLabel: 'Pant',
    category: 'mens',
    sortOrder: 90,
    requiresMeasurements: true,
  },
]);

export const SEEDED_GARMENT_TYPES_BY_KEY: Readonly<
  Record<SeededGarmentTypeKey, SeededGarmentType>
> = Object.freeze(
  Object.fromEntries(SEEDED_GARMENT_TYPES.map((type) => [type.key, type])) as Record<
    SeededGarmentTypeKey,
    SeededGarmentType
  >,
);

/** Measurement units a shop can record in. The shop picks a default; fields may override it. */
export const MEASUREMENT_UNITS = ['in', 'cm'] as const;
export type MeasurementUnit = (typeof MEASUREMENT_UNITS)[number];
export const measurementUnitSchema = z.enum(MEASUREMENT_UNITS);
export const DEFAULT_MEASUREMENT_UNIT: MeasurementUnit = 'in';
